/**
 * Session Management Utilities
 *
 * Platform-agnostic utilities for session management that can be used
 * by both web and mobile applications.
 *
 * Features:
 * - Token expiration checking
 * - Session validation logic
 * - Activity timeout calculations
 * - Token refresh threshold logic
 */

import { SESSION_TIMEOUT } from '../../config/constants';
import { dayjs } from '../date';

import type { AuthMode } from '../../types/auth';

// =============================================================================
// CONSTANTS
// =============================================================================

export const DEFAULT_REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes
export const DEFAULT_SESSION_TIMEOUT = SESSION_TIMEOUT.COOKIE_BASED; // 120 minutes
export const DEFAULT_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

// =============================================================================
// TYPES
// =============================================================================

export interface SessionConfig {
  refreshThreshold?: number;
  sessionTimeout?: number;
  checkInterval?: number;
}

export interface TokenInfo {
  tokenExpiry: number | null;
  authMode: AuthMode;
}

export interface ActivityInfo {
  lastActivity: number;
  sessionTimeout?: number;
}

// =============================================================================
// TOKEN UTILITIES
// =============================================================================

/**
 * Check if a token is expiring soon based on the refresh threshold
 */
export const isTokenExpiringSoon = (
  tokenInfo: TokenInfo,
  refreshThreshold: number = DEFAULT_REFRESH_THRESHOLD
): boolean => {
  if (tokenInfo.authMode !== 'token' || !tokenInfo.tokenExpiry) {
    return false;
  }

  const now = dayjs().valueOf();
  return tokenInfo.tokenExpiry - now < refreshThreshold;
};

/**
 * Check if a token has expired
 */
export const isTokenExpired = (tokenExpiry: number | null): boolean => {
  if (!tokenExpiry) return true;
  return dayjs().valueOf() >= tokenExpiry;
};

/**
 * Get the time until token expiry in milliseconds
 */
export const getTimeUntilExpiry = (tokenExpiry: number | null): number => {
  if (!tokenExpiry) return 0;
  return Math.max(0, tokenExpiry - dayjs().valueOf());
};

/**
 * Get the time until token refresh threshold in milliseconds
 */
export const getTimeUntilRefreshThreshold = (
  tokenExpiry: number | null,
  refreshThreshold: number = DEFAULT_REFRESH_THRESHOLD
): number => {
  if (!tokenExpiry) return 0;
  const timeUntilExpiry = getTimeUntilExpiry(tokenExpiry);
  return Math.max(0, timeUntilExpiry - refreshThreshold);
};

// =============================================================================
// SESSION UTILITIES
// =============================================================================

/**
 * Check if a session is valid based on activity timeout
 */
export const isSessionValid = (
  activityInfo: ActivityInfo,
  sessionTimeout: number = DEFAULT_SESSION_TIMEOUT
): boolean => {
  const now = dayjs().valueOf();
  const timeSinceLastActivity = now - activityInfo.lastActivity;
  return timeSinceLastActivity < sessionTimeout;
};

/**
 * Get the time until session timeout in milliseconds
 */
export const getTimeUntilSessionTimeout = (
  activityInfo: ActivityInfo,
  sessionTimeout: number = DEFAULT_SESSION_TIMEOUT
): number => {
  const now = dayjs().valueOf();
  const timeSinceLastActivity = now - activityInfo.lastActivity;
  return Math.max(0, sessionTimeout - timeSinceLastActivity);
};

/**
 * Check if session should be refreshed based on activity
 */
export const shouldRefreshSession = (
  activityInfo: ActivityInfo,
  sessionTimeout: number = DEFAULT_SESSION_TIMEOUT
): boolean => {
  return !isSessionValid(activityInfo, sessionTimeout);
};

// =============================================================================
// CONFIGURATION UTILITIES
// =============================================================================

/**
 * Get default session configuration
 */
export const getDefaultSessionConfig = (): Required<SessionConfig> => ({
  refreshThreshold: DEFAULT_REFRESH_THRESHOLD,
  sessionTimeout: DEFAULT_SESSION_TIMEOUT,
  checkInterval: DEFAULT_CHECK_INTERVAL,
});

/**
 * Merge session configuration with defaults
 */
export const mergeSessionConfig = (
  config?: SessionConfig
): Required<SessionConfig> => ({
  ...getDefaultSessionConfig(),
  ...config,
});

// =============================================================================
// VALIDATION UTILITIES
// =============================================================================

/**
 * Validate session configuration
 */
export const validateSessionConfig = (config: SessionConfig): string[] => {
  const errors: string[] = [];

  if (config.refreshThreshold !== undefined && config.refreshThreshold < 0) {
    errors.push('Refresh threshold must be non-negative');
  }

  if (config.sessionTimeout !== undefined && config.sessionTimeout < 0) {
    errors.push('Session timeout must be non-negative');
  }

  if (config.checkInterval !== undefined && config.checkInterval < 0) {
    errors.push('Check interval must be non-negative');
  }

  return errors;
};

/**
 * Check if session configuration is valid
 */
export const isSessionConfigValid = (config: SessionConfig): boolean => {
  return validateSessionConfig(config).length === 0;
};
